/**
 * SelectedDayReadout (#2456 Ask 4) — the persistent, click-driven readout that
 * replaces the removed hover tooltip. It sits below the chart (above the Schläge
 * table) and lists the selected bucket's Ist / Prognose / horizon / plan /
 * enabled-Vorjahr values (only the series currently VISIBLE on the chart), each
 * equal to the chart at that bucket. Before any click it shows a one-line hint.
 * The value mapping is the pure, unit-tested ``buildSelectedDayReadout``.
 *
 * #2544-C — a compact TABLE with two value columns rather than a one-value list:
 *
 *   Ernte im Zeitraum — what came in DURING the selected bucket
 *   Kumuliert         — where the season stands AT it
 *
 * Both render in BOTH data modes, which is why this component takes no ``mode``
 * prop: whichever number the plot is drawing, the reader can find it here, so
 * the readout and the plot's selection dot can no longer disagree (they did
 * between #2544-B and #2544-C, when this panel reported the cumulative in
 * Einzelernte too). Showing both also lets a reader answer "good day?" and
 * "good season?" in one glance instead of toggling the mode to compare.
 *
 * The per-period number is the ``ReadoutLine.bucketValue`` the model already
 * carries, printed as-is. NOTHING in this file recomputes it — see
 * ``selectedDayReadout.ts`` for why differencing the cumulative is wrong (the
 * #2455 seed and the #2490 clamps both live in it).
 */
import { useId } from 'react';

import { bucketRangeLabel, type Grain, type OutputChartRow } from './outputSeries';
import { buildSelectedDayReadout, type SelectedDayReadoutOptions } from './selectedDayReadout';

const GRAIN_NOUN: Record<Grain, string> = {
  day: 'Ausgewählter Tag',
  week: 'Ausgewählte Woche',
  month: 'Ausgewählter Monat',
  year: 'Ausgewähltes Jahr',
};

const EMPTY_HINT = 'Tippe auf einen Punkt im Diagramm, um die Werte eines Tages zu sehen.';

/** Module scope, not per cell: the panel renders up to 6 series × 2 columns, and
 *  the formatter depends on nothing. */
const NUMBER_FORMAT = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });

export interface SelectedDayReadoutProps {
  /** The selected chart row, or null (no selection / left the data range → hint). */
  row: OutputChartRow | null;
  grain: Grain;
  unit: string;
  options: SelectedDayReadoutOptions;
  onReset: () => void;
}

export default function SelectedDayReadoutPanel({ row, grain, unit, options, onReset }: SelectedDayReadoutProps) {
  // Before the early return — hooks may not sit behind a condition.
  const titleId = useId();

  if (!row) {
    return (
      <p className="ernteprognose-readout ernteprognose-readout--empty" role="status">
        {EMPTY_HINT}
      </p>
    );
  }

  const lines = buildSelectedDayReadout(row, options);
  /** A null is "outside this series' range" — an em dash, never a 0, which would
   *  claim a measured "nothing happened". A real 0 prints as 0. */
  const cell = (v: number | null) => (v == null ? '—' : `${NUMBER_FORMAT.format(v)} ${unit}`);

  return (
    <div className="ernteprognose-readout" role="group" aria-label={GRAIN_NOUN[grain]}>
      <div className="ernteprognose-readout__head">
        <span className="ernteprognose-readout__title" id={titleId}>
          {/* #2544-A — ``bucketRangeLabel`` spells a WEEK out Mo–So
              ("KW 35 (24.08.–30.08.2026)"); at every other grain it is the plain
              ``bucketLabel``, so day/month/year headings are unchanged. */}
          {GRAIN_NOUN[grain]} — {bucketRangeLabel(row.bucket, grain)}
        </span>
        <button type="button" className="ernteprognose-readout__reset" onClick={onReset}>
          × zurücksetzen
        </button>
      </div>
      {/* ``aria-labelledby`` points at the heading above, so the table's
          accessible name IS the visible title — one string, named once, and a
          screen reader entering the table hears which period it is reading. */}
      <table className="ernteprognose-readout__table" aria-labelledby={titleId}>
        <thead>
          <tr>
            <th scope="col" className="ernteprognose-readout__colhead">
              {/* Hidden rather than absent: the row labels are ``th scope="row"``
                  and are announced on their own either way, but column-wise
                  navigation still reads a header per column, and an empty one
                  there announces as nothing. */}
              <span className="ernteprognose-readout__sr">Datenreihe</span>
            </th>
            <th scope="col" className="ernteprognose-readout__colhead ernteprognose-readout__colhead--value">
              Ernte im Zeitraum
            </th>
            <th scope="col" className="ernteprognose-readout__colhead ernteprognose-readout__colhead--value">
              Kumuliert
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.key}>
              <th scope="row" className="ernteprognose-readout__label">
                {l.label}
              </th>
              <td className="ernteprognose-readout__value">{cell(l.bucketValue)}</td>
              <td className="ernteprognose-readout__value">{cell(l.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
